import type { DiscoveryPricing, PricingOffer } from '../data/discovery';
import { fmtReq, usdCost } from './format';
import {
  callsCostUsd,
  callsWithinBudget,
  displayPrice,
  itemsRateUsd,
  quotedRateUsd,
} from './pricing';

/** Every SKU's published pricing, keyed by slug: what a post quotes from. */
export type PublishedPrices = Readonly<Record<string, DiscoveryPricing>>;

/**
 * One AnyAPI price a post states, named by SKU instead of typed as a number.
 * With only `sku` it is the per-1,000 rate the /api endpoint page headlines.
 */
export interface PriceSpec {
  sku: string;
  /** `call`: what one request costs (the ceiling, on a metered SKU). */
  per?: 'call';
  /** What this many requests cost. */
  calls?: number;
  /** How many whole requests this many USD pay for. */
  budget?: number;
  /** Per 1,000 items when each call returns this many. */
  items?: number;
  /** Quote the most any serving source charges instead of the starting price.
   *  On a SKU billed per result it needs `per`, `calls`, `budget`, or `items`. */
  failover?: boolean;
}

export interface PriceQuote {
  /** USD, or a request count for `budget`: the figure a chart bar plots. */
  value: number;
  /** The figure as a reader sees it. */
  text: string;
}

/**
 * Why a bare `failover` (no per, calls, budget, or items) cannot be quoted, or
 * undefined when it can. The catalog publishes the dearest source's charge per
 * request only, and on a SKU billed per result the bare form quotes per 1,000
 * results, so it has no failover figure in that unit: `airbnb.search` would read
 * "$1.58, rising to $33.10" with no dearer source at all.
 *
 * Exported for the build's check (scripts/contentPrices.ts), which knip does
 * not read, so it refuses the same spec before any page renders.
 *
 * @public
 */
export const bareFailoverRefusal = (sku: string, pricing: DiscoveryPricing): string | undefined => {
  const price = displayPrice(pricing.from);
  return price.metered
    ? `"${sku}" is billed per ${price.unit}, so failover needs per, calls, budget, or items`
    : undefined;
};

const offerFor = (prices: PublishedPrices, spec: PriceSpec): PricingOffer => {
  const pricing: DiscoveryPricing | undefined = prices[spec.sku];
  // Never a blank or a remembered price. The build refuses an unknown SKU or a
  // bare failover it cannot quote before any page renders
  // (scripts/contentPrices.ts); this covers a live navigation after the catalog
  // stopped publishing one or began billing it per result.
  if (!pricing) throw new Error(`The catalog publishes no price for "${spec.sku}"`);
  if (!spec.failover) return pricing.from;
  const bare = [spec.per, spec.calls, spec.budget, spec.items].every((form) => form === undefined);
  const refusal = bare ? bareFailoverRefusal(spec.sku, pricing) : undefined;
  if (refusal) throw new Error(refusal);
  return { model: 'flat', unit: 'request', maxUsd: pricing.failoverMaxUsd };
};

/** Resolves a post's price spec against the published catalog. */
export const quotePrice = (prices: PublishedPrices, spec: PriceSpec): PriceQuote => {
  const offer = offerFor(prices, spec);
  const price = displayPrice(offer);
  if (spec.budget !== undefined) {
    const count = callsWithinBudget(price, spec.budget);
    return { value: count, text: fmtReq(count) };
  }
  let usd = quotedRateUsd(price);
  if (spec.calls !== undefined) usd = callsCostUsd(price, spec.calls);
  else if (spec.items !== undefined) usd = itemsRateUsd(offer, spec.items);
  else if (spec.per === 'call') usd = price.perRequestUsd;
  return { value: usd, text: usdCost(usd) };
};
